import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import SiteHeader from "@/components/site/SiteHeader";
import SiteFooter from "@/components/site/SiteFooter";
import { breadcrumbSchema, breadcrumbsFromPath, organizationSchema } from "@/lib/structuredData";

const Terms = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  const url = "https://yootelco.com/terms";
  const metaTitle = "Terms of Service | Yootelco";
  const metaDescription =
    "Read Yootelco's Terms of Service, covering the rules and conditions for using our enterprise messaging and telecom services.";

  return (
    <main className="min-h-screen bg-white">
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={url} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content={url} />
        <meta property="og:type" content="website" />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDescription} />
        <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema(breadcrumbsFromPath("/terms")))}
        </script>
      </Helmet>

      <SiteHeader />

      {/* Hero */}
      <section className="pb-10 pt-[120px] md:pb-12 md:pt-[140px]" style={{ backgroundImage: "var(--gradient-hero)" }}>
        <div className="container mx-auto max-w-[1080px] text-center">
          <h1 className="mx-auto max-w-[860px] font-display text-[44px] font-bold leading-[1.05] tracking-tight text-foreground md:text-[60px]">
            Terms of Service
          </h1>
          <p className="mx-auto mt-4 max-w-[620px] text-[15px] font-medium text-muted-foreground">
            Last updated: July 8, 2026
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="section-shell">
        <div className="mx-auto max-w-[840px] text-[15px] leading-[1.75] text-muted-foreground md:text-[16px]">
          <p>
            These Terms of Service ("Terms") govern your access to and use of the Yootelco website (yootelco.com) and the A2P SMS, OTP authentication, RCS messaging, voice termination, and SMS API/gateway services (collectively, the "Services") provided by Yootelco ("Yootelco," "we," "us," or "our"). By accessing our website or using our Services, you agree to these Terms.
          </p>

          <h2 className="mt-10 font-display text-[24px] font-bold text-foreground md:text-[28px]">1. Who We Are</h2>
          <p className="mt-4">
            Yootelco is a UK-based enterprise telecom messaging platform, with offices in the United Kingdom, Hong Kong, and Armenia, providing messaging and voice infrastructure to enterprise and fintech clients worldwide.
          </p>

          <h2 className="mt-10 font-display text-[24px] font-bold text-foreground md:text-[28px]">2. Eligibility</h2>
          <p className="mt-4">
            Our Services are intended for business use by enterprises, fintech companies, and authorized representatives acting on their behalf. By using our Services, you confirm you have the authority to enter into these Terms on behalf of your organization.
          </p>

          <h2 className="mt-10 font-display text-[24px] font-bold text-foreground md:text-[28px]">3. Use of Services</h2>
          <p className="mt-4">You agree to:</p>
          <ul className="mt-3 list-disc space-y-2 pl-6">
            <li>Use our Services only for lawful business purposes</li>
            <li>Comply with all applicable telecom regulations, anti-spam laws, and messaging/voice industry standards in the jurisdictions where you operate</li>
            <li>Not use our Services to send unsolicited, fraudulent, deceptive, or illegal content</li>
            <li>Not attempt to interfere with, disrupt, or reverse-engineer our infrastructure or APIs</li>
          </ul>
          <p className="mt-4">
            We reserve the right to suspend or terminate access to the Services for any use that violates these Terms or applicable law.
          </p>

          <h2 className="mt-10 font-display text-[24px] font-bold text-foreground md:text-[28px]">4. Account and Onboarding</h2>
          <p className="mt-4">
            Access to certain Services may require onboarding, account setup, or a signed service agreement. Specific pricing, volume commitments, and service levels will be governed by the applicable order form or contract between you and Yootelco, which takes precedence over these Terms in case of conflict.
          </p>

          <h2 className="mt-10 font-display text-[24px] font-bold text-foreground md:text-[28px]">5. Service Availability</h2>
          <p className="mt-4">
            We aim to provide reliable, carrier-grade service but do not guarantee uninterrupted or error-free operation. Service levels, uptime commitments, and support terms for specific clients may be set out separately in a Service Level Agreement (SLA).
          </p>

          <h2 className="mt-10 font-display text-[24px] font-bold text-foreground md:text-[28px]">6. Intellectual Property</h2>
          <p className="mt-4">
            All content on our website, including text, graphics, logos, and software, is the property of Yootelco or its licensors and is protected by applicable intellectual property laws. You may not copy, reproduce, or distribute our content without prior written permission.
          </p>

          <h2 className="mt-10 font-display text-[24px] font-bold text-foreground md:text-[28px]">7. Client Data and Confidentiality</h2>
          <p className="mt-4">
            Any business or technical information shared with us during onboarding or service use will be handled in accordance with our Privacy Policy and any confidentiality terms agreed in your service contract.
          </p>

          <h2 className="mt-10 font-display text-[24px] font-bold text-foreground md:text-[28px]">8. Limitation of Liability</h2>
          <p className="mt-4">
            To the maximum extent permitted by law, Yootelco shall not be liable for any indirect, incidental, or consequential damages arising from your use of the Services. Our total liability for any claim shall not exceed the amount paid by you for the Services in the twelve (12) months preceding the claim, unless otherwise specified in a separate service agreement.
          </p>

          <h2 className="mt-10 font-display text-[24px] font-bold text-foreground md:text-[28px]">9. Termination</h2>
          <p className="mt-4">
            We may suspend or terminate your access to the Services if you breach these Terms. You may terminate your use of the Services in accordance with the terms of your service agreement.
          </p>

          <h2 className="mt-10 font-display text-[24px] font-bold text-foreground md:text-[28px]">10. Governing Law</h2>
          <p className="mt-4">
            These Terms are governed by the laws of England and Wales. Any disputes arising from these Terms or your use of the Services will be subject to the exclusive jurisdiction of the courts of England and Wales.
          </p>

          <h2 className="mt-10 font-display text-[24px] font-bold text-foreground md:text-[28px]">11. Changes to These Terms</h2>
          <p className="mt-4">
            We may update these Terms from time to time. Material changes will be communicated via email or a notice on our website. Continued use of our Services after changes take effect constitutes acceptance of the updated Terms.
          </p>

          <h2 className="mt-10 font-display text-[24px] font-bold text-foreground md:text-[28px]">12. Contact Us</h2>
          <p className="mt-4">For questions about these Terms, please contact us:</p>
          <ul className="mt-3 list-disc space-y-2 pl-6">
            <li><strong className="text-foreground">Email:</strong> <a href="mailto:info@yootelco.com" className="text-primary hover:underline">info@yootelco.com</a></li>
            <li><strong className="text-foreground">Phone:</strong> <a href="tel:+442070999469" className="text-primary hover:underline">+44 207 0999469</a></li>
            <li><strong className="text-foreground">Registered Office:</strong> 103 High Street, Waltham Cross, Hertfordshire EN8 7AN, United Kingdom</li>
          </ul>
        </div>
      </section>

      <SiteFooter />
    </main>
  );
};

export default Terms;